import { enqueueSnackbar } from '~/utils/notifications';
import { useRecoilState } from 'recoil';
import { BackendClient } from '~/backend/backend-client';
import {
    isJobRunning,
    Job,
    JobPayload,
    jobQueueState,
    TaskState,
} from '~/state/atoms/jobAtoms';
import { WorkflowTypes } from '~/state/atoms/viewerAtoms';

export type NewJobData = {
    id: string;
    name: string;
    type: WorkflowTypes;
    onLoad?: (job: Job) => Promise<void>;
    totalItems?: number;
    payload?: JobPayload;
    client?: BackendClient;
    task?: TaskState;
    inputApprovalStatus?: Job['inputApprovalStatus'];
};

/**
 * A custom hook that provides a API for managing the lifecycle of jobs.
 * It encapsulates the logic for adding, updating, and changing the status of jobs
 * in the global Recoil state.
 *
 * @returns An object containing:
 * - `addJob`: A function to add a new job to the queue.
 * - `updateJob`: A function to update specific properties of an existing job.
 * - `setJobStatus`: A function to change the status of a job and optionally set an error message.
 * - `jobQueue`: The current state of the job queue.
 * - `setJobQueue`: The Recoil setter function for the entire job queue state.
 */
export function useJobManagement() {
    const [jobQueue, setJobQueue] = useRecoilState(jobQueueState);

    const addJob = (details: NewJobData) => {
        const now = Date.now();
        const snackbarId =
            jobQueue.snackbarId ??
            enqueueSnackbar('New task added to queue...', {
                variant: 'jobQueue',
                persist: true,
            });

        setJobQueue((prev) => ({
            snackbarId,
            jobs: [
                {
                    status: 'Queued',
                    uploadedItems: 0,
                    error: undefined,
                    isImported: false,
                    createdAt: now,
                    updatedAt: now,
                    ...details,
                },
                ...prev.jobs,
            ],
        }));
    };

    const updateJob = (
        jobId: string,
        updates: Partial<Omit<Job, 'id' | 'createdAt'>>
    ) => {
        setJobQueue((prevJobQueue) => ({
            ...prevJobQueue,
            jobs: prevJobQueue.jobs.map((job) =>
                job.id === jobId
                    ? { ...job, ...updates, updatedAt: Date.now() }
                    : job
            ),
        }));
    };

    const setJobStatus = (
        jobId: string,
        status: Job['status'],
        errorMessage?: string
    ) => {
        const updates: Partial<Job> = {
            status,
            error: errorMessage,
        };

        if (!isJobRunning(status)) {
            updates.finishedAt = Date.now();
        }

        updateJob(jobId, updates);
    };

    return {
        addJob,
        updateJob,
        setJobStatus,
        jobQueue,
        setJobQueue,
    };
}
